import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
  MAX_PORTFOLIO_IMAGES,
  providersService,
} from '../../shared/services/providers.service';
import type { ProviderServiceItem, ProviderStatus } from '../../shared/types';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { CategoryIcon } from '../../shared/components/ui/CategoryIcon';
import { seedDefaultPortfolioImages } from '../providers/onboardingPortfolio';
import { AddServiceDialog } from './AddServiceDialog';
import { AddPortfolioImageDialog } from './AddPortfolioImageDialog';
import { AddPortfolioImagesBulkDialog } from './AddPortfolioImagesBulkDialog';
import { EditBusinessNameDialog } from './EditBusinessNameDialog';
import { EventSpecializationsCard } from './EventSpecializationsCard';
import { OpeningHoursCard } from './OpeningHoursCard';
import { TravelCoverageCard } from './TravelCoverageCard';

const STATUS_COLOR: Record<ProviderStatus, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  DRAFT: 'info',
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  SUSPENDED: 'default',
};

const STATUS_LABEL: Record<ProviderStatus, string> = {
  DRAFT: 'provider.statusDraft',
  PENDING: 'provider.statusPending',
  APPROVED: 'provider.statusApproved',
  REJECTED: 'provider.statusRejected',
  SUSPENDED: 'provider.statusSuspended',
};

function formatPrice(cents?: number | null, currency?: string | null) {
  if (cents == null || !currency) return null;
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
}

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  // When set, the service dialog opens in edit mode for this row.
  const [editingService, setEditingService] = useState<ProviderServiceItem | null>(null);
  const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
  const [portfolioBulkDialogOpen, setPortfolioBulkDialogOpen] = useState(false);
  const [editNameOpen, setEditNameOpen] = useState(false);

  const mine = useQuery({
    queryKey: ['provider', 'me'],
    queryFn: () => providersService.getMine(),
    retry: false,
  });

  // Completeness drives both the DRAFT-resume banner and a future "what's
  // missing" card.  Gated on having a provider row to avoid a 404 burst
  // when a brand-new user opens the dashboard.
  const completeness = useQuery({
    queryKey: ['provider', 'me', 'completeness'],
    queryFn: () => providersService.myCompleteness(),
    enabled: mine.isSuccess,
    staleTime: 30 * 1000,
  });

  const publish = useMutation({
    mutationFn: () => providersService.publishMine(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['provider', 'me'] });
      qc.invalidateQueries({ queryKey: ['provider', 'me', 'completeness'] });
      notify.success(t('onboarding.publishedSuccess'));
    },
    onError: (err) => notify.error(translate(err)),
  });

  const removeService = useMutation({
    mutationFn: (serviceCatalogId: string) => providersService.removeService(serviceCatalogId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['provider', 'me'] }),
    onError: (err) => notify.error(translate(err)),
  });

  const removeImage = useMutation({
    mutationFn: (imageId: string) => providersService.removePortfolioImage(imageId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['provider', 'me'] }),
    onError: (err) => notify.error(translate(err)),
  });

  /**
   * "Auto-fill 5 photos" — fallback / top-up button. Onboarding now fires
   * the same seed automatically when the pro account is created, so this
   * mostly serves as a retry path (e.g. if the auto-seed failed) or to
   * re-populate after the provider has deleted some defaults.
   */
  const autoFillPortfolio = useMutation({
    mutationFn: async () => {
      const profile = mine.data;
      if (!profile) throw new Error('Provider profile not loaded.');
      const primaryCode = profile.services[0]?.categoryCode ?? 'ADDITIONAL_SERVICES';
      const result = await seedDefaultPortfolioImages({
        categoryCode: primaryCode,
        existingUrls: profile.portfolio.map((img) => img.imageUrl),
      });
      if (result.added === 0) {
        if (result.noImagesAvailable) throw new Error(t('provider.autoFillNoImages'));
        if (result.skippedAlreadyPresent) throw new Error(t('provider.autoFillAlreadyFilled'));
        if (result.firstError) throw result.firstError;
      }
      return result;
    },
    onSuccess: ({ added, firstError }) => {
      qc.invalidateQueries({ queryKey: ['provider', 'me'] });
      notify.success(t('provider.autoFillDone', { count: added }));
      // Partial failure — show the first error too so the provider isn't
      // misled by the success count.
      if (firstError) notify.error(translate(firstError));
    },
    onError: (err) => notify.error(translate(err)),
  });

  if (mine.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  // No provider profile yet → onboarding invitation.
  // Backend returns 200 + null when the user hasn't onboarded; isError
  // is only set when the request itself fails (network / 5xx).
  if (mine.isError || mine.data === null || mine.data === undefined) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 4, md: 8 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 88,
              height: 88,
              mx: 'auto',
              mb: 3,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main}1F 0%, ${theme.palette.secondary.main}1F 100%)`,
              color: 'primary.main',
            }}
          >
            <StorefrontIcon sx={{ fontSize: 44 }} />
          </Box>
          <Typography variant="h4" sx={{ mb: 1.5 }}>
            {t('provider.becomeTitle')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4, fontSize: '1.0625rem', lineHeight: 1.55 }}>
            {t('provider.becomeSubtitle')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => navigate('/become-provider')}
          >
            {t('nav.becomeProvider')}
          </Button>
        </Paper>
      </Container>
    );
  }

  const p = mine.data!;
  const cover = p.portfolio[0]?.imageUrl;
  const initials = p.businessName
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {/* HEADER CARD */}
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          mb: 3,
        }}
      >
        <Box
          sx={{
            height: { xs: 110, sm: 140 },
            backgroundColor: (theme) => theme.palette.primary.main,
            backgroundImage: cover
              ? `linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.4) 100%), url(${cover})`
              : (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <Box sx={{ p: { xs: 2, sm: 3 }, pt: 0 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'flex-end' }}
            sx={{ mt: { xs: -5, sm: -6 } }}
          >
            <Avatar
              sx={{
                width: { xs: 80, sm: 96 },
                height: { xs: 80, sm: 96 },
                fontSize: '1.75rem',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontWeight: 800,
                bgcolor: 'background.paper',
                color: 'primary.main',
                border: '4px solid',
                borderColor: 'background.paper',
                boxShadow: 4,
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0, pt: { xs: 1, sm: 0 }, pb: { sm: 1 } }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word',
                  }}
                >
                  {p.businessName}
                </Typography>
                <IconButton
                  size="small"
                  aria-label={t('provider.editName')}
                  onClick={() => setEditNameOpen(true)}
                  sx={{ color: 'text.secondary' }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <LocationOnIcon
                  sx={{ fontSize: '1rem', color: 'text.secondary', flexShrink: 0 }}
                />
                <Typography
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {p.cityName ?? p.city?.name ?? ''}
                  {(p.cityName || p.city?.name) && ', '}
                  {p.countryCode ?? p.city?.country ?? ''}
                </Typography>
              </Stack>
            </Box>
            <Chip
              label={t(STATUS_LABEL[p.status])}
              color={STATUS_COLOR[p.status]}
              sx={{
                fontWeight: 600,
                maxWidth: '100%',
                alignSelf: { xs: 'flex-start', sm: 'flex-end' },
              }}
            />
          </Stack>
        </Box>
      </Paper>

      {p.status === 'DRAFT' && (
        <Alert
          severity="info"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            completeness.data?.readyToPublish ? (
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={publish.isPending}
                onClick={() => publish.mutate()}
              >
                {publish.isPending ? t('common.loading') : t('onboarding.publishNow')}
              </Button>
            ) : (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => navigate('/become-provider')}
              >
                {t('onboarding.resumeCta')}
              </Button>
            )
          }
        >
          {completeness.data?.readyToPublish
            ? t('onboarding.draftReadyBanner', { score: completeness.data?.score ?? 0 })
            : t('onboarding.draftBanner', { score: completeness.data?.score ?? 0 })}
        </Alert>
      )}

      {p.status === 'REJECTED' && p.rejectedReason && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {t('provider.rejectedReason', { reason: p.rejectedReason })}
        </Alert>
      )}

      {p.status === 'APPROVED' && (
        <Alert
          severity="success"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button
              component={RouterLink}
              to={`/providers/${p.id}`}
              size="small"
              color="inherit"
              variant="outlined"
            >
              {t('search.viewProfile')}
            </Button>
          }
        >
          {t('provider.statusApproved')}
        </Alert>
      )}

      {/* OPENING HOURS */}
      <OpeningHoursCard />

      {/* TRAVEL COVERAGE (V48) */}
      <TravelCoverageCard
        canTravel={p.canTravel ?? false}
        travelRadiusKm={p.travelRadiusKm}
        cityName={p.cityName ?? p.city?.name ?? ''}
      />

      {/* SERVICES */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: 2, mt: 1 }}
      >
        <Typography variant="h5" sx={{ flex: 1 }}>
          {t('provider.services')}
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          size="small"
          onClick={() => setServiceDialogOpen(true)}
          sx={{
            whiteSpace: 'nowrap',
            alignSelf: { xs: 'flex-start', sm: 'auto' },
          }}
        >
          {t('provider.addService')}
        </Button>
      </Stack>
      {p.services.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 4, borderRadius: 3, borderStyle: 'dashed', textAlign: 'center', mb: 4 }}
        >
          <Typography color="text.secondary">{t('provider.noServices')}</Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 4 }}>
          {p.services.map((s) => (
            <Paper
              key={s.id}
              elevation={0}
              sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: (theme) =>
                      `linear-gradient(135deg, ${theme.palette.primary.main}1F 0%, ${theme.palette.secondary.main}1F 100%)`,
                    color: 'primary.main',
                    flexShrink: 0,
                  }}
                >
                  <CategoryIcon code={s.categoryCode ?? undefined} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                    {s.serviceName}
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.25 }}>
                    {formatPrice(s.customPriceCents, s.currency) && (
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {s.priceType === 'STARTING_FROM'
                          ? `${t('provider.fromPrice')} ${formatPrice(s.customPriceCents, s.currency)}`
                          : formatPrice(s.customPriceCents, s.currency)}
                      </Typography>
                    )}
                    {s.durationMinutes && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <AccessTimeRoundedIcon
                          sx={{ fontSize: '0.95rem', color: 'text.secondary' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {s.durationMinutes} min
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Box>
                <IconButton
                  size="small"
                  aria-label={t('common.edit')}
                  onClick={() => setEditingService(s)}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  disabled={removeService.isPending}
                  onClick={() => removeService.mutate(s.id)}
                  aria-label={t('common.delete')}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {/* EVENT SPECIALIZATIONS */}
      <EventSpecializationsCard selected={p.eventSpecializations ?? []} />

      {/* PORTFOLIO — the backend caps at MAX_PORTFOLIO_IMAGES; mirror the
          count here so the buttons can't be clicked past the limit. */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: 2 }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5">
            {t('provider.portfolio')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('provider.portfolioCount', {
              count: p.portfolio.length,
              max: MAX_PORTFOLIO_IMAGES,
              defaultValue: '{{count}} / {{max}} photos',
            })}
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          alignSelf={{ xs: 'flex-start', sm: 'auto' }}
          sx={{ flexWrap: 'wrap', gap: 1 }}
        >
          <Button
            startIcon={
              autoFillPortfolio.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoAwesomeRoundedIcon />
              )
            }
            variant="outlined"
            size="small"
            onClick={() => autoFillPortfolio.mutate()}
            disabled={
              autoFillPortfolio.isPending
              || p.portfolio.length >= MAX_PORTFOLIO_IMAGES
            }
            sx={{ whiteSpace: 'nowrap' }}
          >
            {t('provider.autoFillCta')}
          </Button>
          <Button
            startIcon={<PhotoLibraryRoundedIcon />}
            variant="outlined"
            size="small"
            onClick={() => setPortfolioBulkDialogOpen(true)}
            disabled={p.portfolio.length >= MAX_PORTFOLIO_IMAGES}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {t('provider.addMultiple', { defaultValue: 'Plusieurs photos' })}
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            size="small"
            onClick={() => setPortfolioDialogOpen(true)}
            disabled={p.portfolio.length >= MAX_PORTFOLIO_IMAGES}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {t('provider.addImageUrl')}
          </Button>
        </Stack>
      </Stack>
      {p.portfolio.length >= MAX_PORTFOLIO_IMAGES && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('provider.portfolioMaxReached', {
            max: MAX_PORTFOLIO_IMAGES,
            defaultValue:
              'Maximum {{max}} photos atteint. Supprimez-en une pour en ajouter une nouvelle.',
          })}
        </Typography>
      )}
      {p.portfolio.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 4, borderRadius: 3, borderStyle: 'dashed', textAlign: 'center' }}
        >
          <Typography color="text.secondary">{t('provider.noPortfolio')}</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {p.portfolio.map((img) => {
            const priceLabel = formatPrice(img.priceCents, img.currency);
            return (
              <Grid item xs={6} sm={4} md={3} key={img.id}>
                <Box
                  sx={{
                    position: 'relative',
                    aspectRatio: '1 / 1',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover .delete-btn': { opacity: 1 },
                  }}
                >
                  <Box
                    component="img"
                    src={img.imageUrl}
                    alt={img.caption ?? p.businessName}
                    loading="lazy"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />

                  {priceLabel && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: 8,
                        px: 1.25,
                        py: 0.5,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(0,0,0,0.65)',
                        backdropFilter: 'blur(6px)',
                        color: 'common.white',
                        fontWeight: 700,
                        fontSize: '0.8125rem',
                      }}
                    >
                      {priceLabel}
                    </Box>
                  )}

                  <IconButton
                    className="delete-btn"
                    size="small"
                    color="error"
                    disabled={removeImage.isPending}
                    onClick={() => removeImage.mutate(img.id)}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      bgcolor: 'rgba(255,255,255,0.95)',
                      opacity: { xs: 1, md: 0 },
                      transition: 'opacity 200ms ease',
                      '&:hover': { bgcolor: 'common.white' },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      <AddServiceDialog open={serviceDialogOpen} onClose={() => setServiceDialogOpen(false)} />
      {/* Edit-mode instance — separate from the add-mode one so the open
          state for each is independent and we never flicker the wrong title. */}
      <AddServiceDialog
        open={Boolean(editingService)}
        service={editingService}
        onClose={() => setEditingService(null)}
      />
      <AddPortfolioImageDialog
        open={portfolioDialogOpen}
        onClose={() => setPortfolioDialogOpen(false)}
      />
      <AddPortfolioImagesBulkDialog
        open={portfolioBulkDialogOpen}
        onClose={() => setPortfolioBulkDialogOpen(false)}
        currentCount={p.portfolio.length}
      />
      <EditBusinessNameDialog
        open={editNameOpen}
        initialName={p.businessName}
        onClose={() => setEditNameOpen(false)}
      />
    </Container>
  );
}
